/**
 * useDocumentComments - Hook for managing inline comments on documents
 *
 * Features:
 * - Fetch comments for a document
 * - Create new comments
 * - Reply to comments
 * - Resolve comments
 * - Delete comments
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { InlineComment, CommentReply } from '../components/documentation/InlineComment';
import { documentsApi, type DocumentComment } from '../api/documents';

// Transform API comment to InlineComment type
function transformComment(comment: DocumentComment): InlineComment {
  return {
    id: comment.id,
    documentId: comment.document_id,
    selectionStart: comment.selection_start,
    selectionEnd: comment.selection_end,
    selectedText: comment.selected_text,
    content: comment.content,
    authorId: comment.author_id,
    authorName: comment.author_name,
    authorType: comment.author_type,
    parentId: comment.parent_id,
    status: comment.status,
    createdAt: comment.created_at,
    updatedAt: comment.updated_at,
  };
}

interface UseDocumentCommentsOptions {
  documentId: string;
  projectId: string;
  userId: string;
  userName: string;
  enabled?: boolean;
}

export function useDocumentComments({
  documentId,
  projectId,
  userId,
  userName,
  enabled = true,
}: UseDocumentCommentsOptions) {
  const queryClient = useQueryClient();
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);

  // Fetch comments
  const {
    data: queryData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['document-comments', projectId, documentId],
    queryFn: async () => {
      const apiComments = await documentsApi.comments.list(documentId, projectId);
      return apiComments.map(transformComment);
    },
    enabled: enabled && !!documentId && !!projectId,
  });

  // IMPORTANT: `data = []` destructure default would create a fresh `[]`
  // reference on every render while data is undefined, cascading through
  // useMemo deps downstream and driving React #185 in consumers that use
  // `comments` in useEffect deps. Memoize the fallback instead.
  const comments = useMemo(() => queryData ?? [], [queryData]);

  // Group comments and replies
  const { rootComments, repliesMap } = useMemo(() => {
    const roots: InlineComment[] = [];
    const replies = new Map<string, CommentReply[]>();

    comments.forEach((comment) => {
      if (comment.parentId) {
        const existing = replies.get(comment.parentId) || [];
        existing.push(comment as unknown as CommentReply);
        replies.set(comment.parentId, existing);
      } else {
        roots.push(comment);
      }
    });

    // Sort replies by date
    replies.forEach((replyList) => {
      replyList.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    });

    return { rootComments: roots, repliesMap: replies };
  }, [comments]);

  // Create comment mutation
  const createMutation = useMutation({
    mutationFn: async (data: {
      selectionStart: number;
      selectionEnd: number;
      selectedText: string;
      content: string;
    }) => {
      const apiComment = await documentsApi.comments.create(documentId, projectId, {
        selection_start: data.selectionStart,
        selection_end: data.selectionEnd,
        selected_text: data.selectedText,
        content: data.content,
        author_id: userId,
        author_name: userName,
        author_type: 'user',
      });
      return transformComment(apiComment);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-comments', projectId, documentId] });
    },
  });

  // Reply mutation
  const replyMutation = useMutation({
    mutationFn: async ({ commentId, content }: { commentId: string; content: string }) => {
      const apiComment = await documentsApi.comments.reply(documentId, projectId, commentId, {
        content,
        author_id: userId,
        author_name: userName,
        author_type: 'user',
      });
      return transformComment(apiComment);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-comments', projectId, documentId] });
    },
  });

  // Resolve mutation
  const resolveMutation = useMutation({
    mutationFn: async (commentId: string) => {
      const apiComment = await documentsApi.comments.update(documentId, projectId, commentId, {
        status: 'resolved',
      });
      return transformComment(apiComment);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-comments', projectId, documentId] });
      setActiveCommentId(null);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (commentId: string) =>
      documentsApi.comments.delete(documentId, projectId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-comments', projectId, documentId] });
      setActiveCommentId(null);
    },
  });

  // Actions
  const createComment = useCallback(
    (data: {
      selectionStart: number;
      selectionEnd: number;
      selectedText: string;
      content: string;
    }) => {
      return createMutation.mutateAsync(data);
    },
    [createMutation]
  );

  const replyToComment = useCallback(
    (commentId: string, content: string) => {
      return replyMutation.mutateAsync({ commentId, content });
    },
    [replyMutation]
  );

  const resolveComment = useCallback(
    (commentId: string) => {
      return resolveMutation.mutateAsync(commentId);
    },
    [resolveMutation]
  );

  const deleteComment = useCallback(
    (commentId: string) => {
      return deleteMutation.mutateAsync(commentId);
    },
    [deleteMutation]
  );

  // Get comment at specific position
  const getCommentAtPosition = useCallback(
    (position: number): InlineComment | undefined => {
      return rootComments.find(
        (c) => position >= c.selectionStart && position <= c.selectionEnd
      );
    },
    [rootComments]
  );

  // Get replies for a comment
  const getReplies = useCallback(
    (commentId: string): CommentReply[] => {
      return repliesMap.get(commentId) || [];
    },
    [repliesMap]
  );

  return {
    comments: rootComments,
    repliesMap,
    isLoading,
    error,
    activeCommentId,
    setActiveCommentId,
    createComment,
    replyToComment,
    resolveComment,
    deleteComment,
    getCommentAtPosition,
    getReplies,
    isCreating: createMutation.isPending,
    isReplying: replyMutation.isPending,
    isResolving: resolveMutation.isPending,
  };
}

export default useDocumentComments;
